






































































// Components
import SearchList from '@/components/GlobalSearch/SearchList.vue';
import VehicleDropdown from './VehicleDropdown.vue';
import { BTooltip } from 'bootstrap-vue';
import ZeroCoverage from '@/components/Onramp/ZeroCoverage.vue';

//Types
import { Service } from '@/types/Services';
import { ServiceSelectionCategory } from '@/types/resources/ServiceSelectionCategories';
import { ServiceSelectionInterview } from '@/types/resources/ServiceSelectionInterview';
import { isNullish } from '@/utilities';
import segmentServices from '@/services/segmentServices';
import { Subscription } from '@/types/microservices/Subscriptions';
import { ZipcodeLocation } from '@/types/ZipcodeLocation';
import CombinedComponent, { userMixin } from '@/mixins';
import { AppointmentService } from '@/types/AppointmentService';
import { findPostalCode, postalCodeFromGeocoderResult, POSTAL_CODE_INPUT_MAX_LENGTH } from '@/concerns/postalCode';
import TurboService from '@/services/microservices/turboService';

const ZIP_TIP_TEXT = 'Please provide a ZIP or postal code so we can get you estimates from shops nearby.';
// Matches the stacked hero layout's breakpoint in the styles below.
const NARROW_VIEWPORT_QUERY = '(max-width: 575.98px)';

export default CombinedComponent(userMixin).extend({
  name: 'GlobalSearch',

  components: {
    SearchList,
    BTooltip,
    VehicleDropdown,
    ZeroCoverage
  },

  props: {
    buttonClasses: {
      type: String,
      required: false,
      default: 'btn-primary'
    }
  },

  data() {
    return {
      postalCodeInputMaxLength: POSTAL_CODE_INPUT_MAX_LENGTH,
      loading: false,
      loadingTargetMarket: false,
      zipcode: this.$store.getters['user/getZipcode'] || '',
      zipTipShown: false,
      zipTipText: ZIP_TIP_TEXT,
      narrowViewport: false,
      heroClassOnRoot: false,
      narrowViewportQuery: null as MediaQueryList | null,
      serviceForOnRamp: {} as any,
      routeForOnramp: '',
      defaultDataHover: false,
      outOfMarketZipcode: '',
      currentLocation: null as ZipcodeLocation | null
    };
  },

  computed: {
    zipTipStacked(): boolean {
      return this.narrowViewport && (this.homepage || this.heroClassOnRoot);
    },

    // Use an object so Vue leaves the tooltip's own aria-describedby alone.
    zipInputDescribedBy(): Record<string, string> {
      return this.zipTipShown && this.zipTipStacked ? { 'aria-describedby': 'global-search-zip-tip' } : {};
    },

    homepage(): boolean {
      return ['home', 'openbayplus'].includes(this.$route.name ?? '');
    },

    services(): Service[] {
      return this.$store.getters['services/getServices'];
    },

    appointmentServices(): AppointmentService[] {
      return this.$store.getters['services/getAppointmentServices'];
    },

    searchData(): any[] {
      return [...this.services, ...this.appointmentServices];
    },

    defaultData(): ServiceSelectionInterview[] {
      return this.popularServices.interviews;
    },

    fuseSearchKeys(): any[] {
      const serviceKeys = [
        {
          name: 'name',
          weight: 0.7
        },
        {
          name: 'category_name',
          weight: 0.3
        }
      ];
      return serviceKeys;
    },

    defaultDataLabel(): string {
      return 'Popular Services';
    },

    placeholder(): string {
      if (document.documentElement.clientWidth <= 768) {
        return 'Search services';
      } else {
        return 'Search services or describe problem';
      }
    },

    popularServices(): ServiceSelectionCategory {
      return this.$store.getters['services/getPopularServiceInterviews'] || {};
    },

    sortedServices(): ServiceSelectionInterview[] {
      let toSort = [...this.popularServices.interviews];
      return toSort.sort((a, b) => a.weight - b.weight);
    },

    validZipcode(): boolean {
      return findPostalCode(this.zipcode) !== null;
    },

    globalUserZipcode(): string {
      return this.$store.getters['user/getZipcode'] || '';
    },

    openbayPlusSubscription(): Subscription | undefined {
      return this.$store.getters['user/getSubscriptions'][0];
    }
  },

  watch: {
    globalUserZipcode(newVal: string, oldVal: string): void {
      if (newVal !== oldVal) this.zipcode = newVal;
    },

    zipcode(newVal: string): void {
      if (this.zipTipShown && findPostalCode(newVal) !== null) {
        this.zipTipShown = false;
      }
    },

    popularServices: {
      immediate: true,
      handler: function () {
        if (isNullish(this.popularServices)) {
          this.loading = true;
        } else {
          this.loading = false;
        }
      }
    }
  },

  created(): void {
    if (this.services.length <= 1) {
      this.loading = true;
      this.$store.dispatch('services/fetchServices').then(() => {
        this.loading = false;
      });
    }
  },

  mounted(): void {
    this.heroClassOnRoot = this.$el.classList.contains('hero-banner-search');
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const query = window.matchMedia(NARROW_VIEWPORT_QUERY);
      this.narrowViewportQuery = query;
      this.narrowViewport = query.matches;
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', this.onViewportChange);
      } else {
        query.addListener(this.onViewportChange);
      }
    }
  },

  beforeDestroy(): void {
    const query = this.narrowViewportQuery;
    if (query) {
      if (typeof query.removeEventListener === 'function') {
        query.removeEventListener('change', this.onViewportChange);
      } else {
        query.removeListener(this.onViewportChange);
      }
    }
  },

  methods: {
    onViewportChange(event: MediaQueryListEvent): void {
      this.narrowViewport = event.matches;
    },

    setServiceForOnramp(service: Service | AppointmentService): void {
      if (service.type) {
        this.routeForOnramp = 'service';
        this.serviceForOnRamp = service;
      } else {
        const serviceId = service.id.toString();
        segmentServices.trackGeneral('Service Search Submitted', {
          service_id: serviceId
        });
        this.routeForOnramp = 'service';
        this.serviceForOnRamp = { serviceId };
      }
    },

    goToAddVehicle(): void {
      this.$store.commit('onrampCart/clearOwnedVehicle');
      this.$store.commit('onrampCart/clearVehicleToCreate');
      this.$store.commit('user/setZipcode', this.zipcode);
      this.$store.commit('onrampCart/setZipcode', this.zipcode);
      this.$router.push({ name: 'add-vehicle-onramp' });
    },

    selectDefaultOption(selectedOption: ServiceSelectionInterview): void {
      segmentServices.trackGeneral('Service Added', {
        service_selection_method: 'Popular Services'
      });
      this.routeForOnramp = 'interview';
      this.serviceForOnRamp = {
        interview: JSON.stringify(selectedOption)
      };
    },

    clearSelectedService(): void {
      this.serviceForOnRamp = {};
      this.routeForOnramp = '';
    },

    goToAllServices(): void {
      this.$store.commit('onrampCart/reset');
      if (this.zipcode?.length) {
        if (!this.validZipcode) return this.showZipTip();
        this.zipcode = findPostalCode(this.zipcode) as string;
        this.checkZipcodeCoverage().then(() => {
          this.$router.push({
            name: 'service',
            params: { zipcode: this.zipcode }
          });
        });
      } else {
        this.$router.push({ name: 'service' });
      }
    },

    updateUserLocation(zipcode: string): Promise<void> {
      return this.$store.dispatch('user/fetchLocation', zipcode).then((location: ZipcodeLocation) => {
        this.currentLocation = location;
        this.$store.commit('onrampCart/setLocation', location);
      });
    },

    verifyZipcodeIsInMarket(zipcode: string): Promise<void> {
      if (!zipcode) return Promise.reject(new Error('Zipcode cannot be blank'));
      return new TurboService().getTargetMarketCheck(zipcode).then((tm) => {
        if (!tm.in_market) throw new Error('Zipcode is otm');
      });
    },

    checkZipcodeCoverage(): Promise<void> {
      this.loadingTargetMarket = true;
      this.outOfMarketZipcode = '';

      return this.verifyZipcodeIsInMarket(this.zipcode)
        .then(() => this.updateUserLocation(this.zipcode))
        .catch((error) => {
          this.outOfMarketZipcode = this.zipcode;
          this.zipcode = '';
          throw error;
        })
        .finally(() => (this.loadingTargetMarket = false));
    },

    startOnramp(): void {
      if (!this.validZipcode) return this.showZipTip();
      this.zipcode = findPostalCode(this.zipcode) as string;
      this.checkZipcodeCoverage()
        .then(() => {
          this.$store.commit('onrampCart/reset');
          if (this.routeForOnramp !== '') {
            let location = this.currentLocation;
            // user selected an appointment service
            if ('type' in this.serviceForOnRamp && location) {
              const type = this.serviceForOnRamp['type'] as string;
              if (type === 'carWash') {
                this.$router.push({
                  name: 'car-wash',
                  params: { zipcode: location.zipcode }
                });
              } else {
                this.$router.push({
                  name: 'auto-repair-service-locations',
                  params: { stateAbbreviation: location.state!.toString(), city: location.city!.toString() },
                  query: { type: type }
                });
              }
            } else {
              let params = {
                ...this.serviceForOnRamp,
                zipcode: this.zipcode
              };
              this.$router.push({
                name: this.routeForOnramp,
                params
              });
            }
          } else {
            this.$router.push({
              name: 'service',
              params: { zipcode: this.zipcode }
            });
          }
        })
        .catch(() => {
          // If the zipcode isn't valid, it will bubble up here.
          // The logic for displaying the ZeroCoverage modal is handled by the modal component,
          // so this catch block can be empty.
        });
    },

    showZipTip(): void {
      this.zipTipShown = true;
    },

    onZipTipShowUpdate(shown: boolean): void {
      if (!shown && !this.zipTipStacked) {
        this.zipTipShown = false;
      }
    },

    checkGeoLocationPermission(): void {
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((res) => {
          if (res.state === 'granted') {
            segmentServices.trackGeneral('Responded to Location Request', {
              locationPermissionState: 'granted'
            });
          }

          // Asking for location regardless of permissions, as it triggers permissions request
          // if the current status is 'pending'
          this.askForLocation();
        });
      }
    },

    askForLocation(): void {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            var lat = position.coords.latitude;
            var long = position.coords.longitude;
            var point = new google.maps.LatLng(lat, long);
            new google.maps.Geocoder().geocode({ location: point }, (res, _status) => {
              const zipcode = postalCodeFromGeocoderResult(res[0]);
              if (zipcode) {
                if (this.zipcode === '') {
                  this.zipcode = zipcode;
                  this.$store.dispatch('user/fetchLocation', this.zipcode);
                }
              } else {
                this.zipcode = this.globalUserZipcode;
              }
            });
          },
          (_error) => {
            segmentServices.trackGeneral('Responded to Location Request', {
              locationPermissionState: 'denied'
            });
          },
          {
            timeout: 5000
          }
        );
      }
    }
  }
});
